'use client'

import type { ReactNode } from 'react'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircleIcon, InfoIcon, WarningIcon, XIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Toast. Built in the system's style: Colors/surface/overlay, default border,
 * 8px corners, the popover shadow, a 20px status icon, Roboto / text-base / medium title and
 * Roboto / text-sm description. Bottom right, dismissed after 4 seconds or with the close button.
 */
type ToastTone = 'success' | 'info' | 'warning'

interface ToastItem {
    id: number
    title: string
    description?: string
    tone: ToastTone
}

interface ToastContextValue {
    toast: (toast: Omit<ToastItem, 'id' | 'tone'> & { tone?: ToastTone }) => void
}

const ToastContext = createContext<ToastContextValue>({ toast: () => undefined })

const toneIcon: Record<ToastTone, ReactNode> = {
    success: <CheckCircleIcon className='text-status-success-foreground' aria-hidden='true' />,
    info: <InfoIcon className='text-status-info-foreground' aria-hidden='true' />,
    warning: <WarningIcon className='text-status-warning-foreground' aria-hidden='true' />
}

function ToastCard({ item, onDismiss }: { item: ToastItem, onDismiss: (id: number) => void }) {
    useEffect(() => {
        const timer = window.setTimeout(() => onDismiss(item.id), 4000)
        return () => window.clearTimeout(timer)
    }, [item.id, onDismiss])

    return (
        <div
            role='status'
            data-slot='toast'
            className='pointer-events-auto flex w-full items-start gap-3 rounded-lg border border-border-default bg-surface-overlay p-4 text-text-primary shadow-popover animate-in fade-in slide-in-from-bottom-2 duration-200 [&>svg]:size-5 [&>svg]:shrink-0'
        >
            {toneIcon[item.tone]}
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <p className='text-base font-medium text-text-primary'>
                    {item.title}
                </p>
                {item.description && (
                    <p className='text-sm text-text-secondary'>
                        {item.description}
                    </p>
                )}
            </div>
            <button
                type='button'
                onClick={() => onDismiss(item.id)}
                aria-label='Dismiss'
                className='inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-sm text-icon-secondary transition-colors outline-none hover:text-icon-hover focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-4'
            >
                <XIcon aria-hidden='true' />
            </button>
        </div>
    )
}

export function ToastProvider({ children }: { children: ReactNode }) {
    const [items, setItems] = useState<ToastItem[]>([])
    const nextId = useRef(1)

    const dismiss = useCallback((id: number) => setItems(current => current.filter(item => item.id !== id)), [])

    const toast = useCallback<ToastContextValue['toast']>(({ tone = 'success', ...rest }) => {
        const id = nextId.current++
        setItems(current => [...current.slice(-2), { id, tone, ...rest }])
    }, [])

    const value = useMemo(() => ({ toast }), [toast])

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div aria-live='polite' className={cn('pointer-events-none fixed right-4 bottom-4 z-60 flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2')}>
                {items.map(item => <ToastCard key={item.id} item={item} onDismiss={dismiss} />)}
            </div>
        </ToastContext.Provider>
    )
}

export const useToast = () => useContext(ToastContext)

export type { ToastTone }
